import { Injectable } from '@angular/core';
import { HttpStatusCode } from '../../shared/enums/http-status-codes.enum';
import { AllowedErrorUrl } from '../contract/allowed-error-url.contract';

@Injectable({
  providedIn: 'root',
})
export class UrlErrorService {
  private readonly offlineUrls: string[] = [
    'api/v1/equipments/disposition',
    'api/v1/bulk-items/disposition',
    'api/v1/projects/disposition',
    'api/v1/stocks/disposition',
    'api/v1/amounts/disposition',
    'api/v1/project-amounts/disposition',
  ];
  private readonly pollingApiUrls: string[] = [
    'api/v1/communication/chat/unread',
    'api/v1/notifications/equipmentalarms/current-active',
    'api/v1/notifications/alarms/current-active',
  ];
  private readonly allowedErrorUrls: AllowedErrorUrl[] = [
    {
      url: 'beut-push-service:7216/api/v1/push/register',
      errorCodes: [HttpStatusCode.UNKNOWN_ERROR, HttpStatusCode.UNAUTHORIZED],
      throwObservableError: true,
    },
    {
      url: 'beut-push-service:7216/api/v1/push/unregister',
      errorCodes: [HttpStatusCode.UNKNOWN_ERROR, HttpStatusCode.UNAUTHORIZED],
      throwObservableError: true,
    },
    {
      url: 'api/v1/osp/connector',
      errorCodes: [HttpStatusCode.NOT_FOUND],
    },
    {
      url: 'api/v1/bulk-items/disposition',
      errorCodes: [HttpStatusCode.GATEWAY_TIMEOUT, HttpStatusCode.UNAUTHORIZED],
    },
    {
      url: 'api/v1/communication/chat/unread',
      errorCodes: [
        HttpStatusCode.UNKNOWN_ERROR,
        HttpStatusCode.GATEWAY_TIMEOUT,
        HttpStatusCode.SERVICE_UNAVAILABLE,
        HttpStatusCode.BAD_GATEWAY,
        HttpStatusCode.UNAUTHORIZED,
      ],
    },
    {
      url: 'api/v1/notifications/alarms/current-active',
      errorCodes: [
        HttpStatusCode.UNKNOWN_ERROR,
        HttpStatusCode.GATEWAY_TIMEOUT,
        HttpStatusCode.SERVICE_UNAVAILABLE,
        HttpStatusCode.BAD_GATEWAY,
        HttpStatusCode.UNAUTHORIZED,
      ],
    },
    {
      url: 'api/v1/equipments/by-scan-code',
      errorCodes: [HttpStatusCode.NOT_FOUND, HttpStatusCode.FORBIDDEN, HttpStatusCode.LOCKED],
      throwObservableError: true,
    },
    {
      url: 'api/v1/scancodes/scan-code-in-use',
      errorCodes: [HttpStatusCode.NOT_FOUND],
      throwObservableError: true,
    },
    {
      url: 'api/v1/equipments/add-label',
      errorCodes: [HttpStatusCode.NOT_MODIFIED],
    },
    {
      url: 'api/v1/geofences/set-alarming',
      errorCodes: [HttpStatusCode.NOT_MODIFIED],
    },
    {
      url: 'api/v1/equipments/filterable-equipment-types',
      errorCodes: [HttpStatusCode.GATEWAY_TIMEOUT, HttpStatusCode.UNAUTHORIZED],
    },
    {
      url: 'api/v1/equipments/disposition',
      errorCodes: [HttpStatusCode.GATEWAY_TIMEOUT, HttpStatusCode.UNAUTHORIZED],
    },
    {
      url: 'api/v1/notifications/equipmentalarms/current-active',
      errorCodes: [
        HttpStatusCode.GATEWAY_TIMEOUT,
        HttpStatusCode.SERVICE_UNAVAILABLE,
        HttpStatusCode.BAD_GATEWAY,
        HttpStatusCode.UNAUTHORIZED,
      ],
    },
    {
      url: 'api/v1/projects/disposition',
      errorCodes: [HttpStatusCode.GATEWAY_TIMEOUT, HttpStatusCode.UNAUTHORIZED],
    },
    {
      url: 'api/v1/stocks/disposition',
      errorCodes: [HttpStatusCode.GATEWAY_TIMEOUT, HttpStatusCode.UNAUTHORIZED],
    },
    {
      url: 'api/v1/docuware/find-documents',
      errorCodes: [HttpStatusCode.INTERNAL_SERVER_ERROR],
      throwObservableError: true,
    },
    {
      url: 'api/v1/topfact/find-documents',
      errorCodes: [HttpStatusCode.SERVICE_UNAVAILABLE],
      throwObservableError: true,
    },
    {
      url: 'api/v1/projects/type-of-use',
      errorCodes: [HttpStatusCode.BAD_REQUEST, HttpStatusCode.INTERNAL_SERVER_ERROR],
      throwObservableError: true,
    },
    {
      url: '/api/v1/stocks',
      errorCodes: [HttpStatusCode.FORBIDDEN],
      throwObservableError: true,
    },
  ];

  public isExpectedOfflineOrAllowedErrorUrl(isOnline: boolean, url: string, errorCode: number): boolean {
    return (
      this.isHealthUrl(url) || this.isAllowedErrorUrl(url, errorCode) || (!isOnline && this.isExpectedOfflineUrl(url))
    );
  }

  public shouldThrowObservable(url: string, errorCode: number): boolean {
    return (
      !this.isAllowedErrorUrl(url, errorCode) ||
      this.allowedErrorUrls.some(
        (allowedErrorUrl: AllowedErrorUrl) =>
          url.includes(allowedErrorUrl.url) &&
          (!errorCode || allowedErrorUrl.errorCodes.includes(errorCode)) &&
          allowedErrorUrl.throwObservableError,
      )
    );
  }

  private isHealthUrl(url: string): boolean {
    return url.endsWith('/health');
  }

  private isAllowedErrorUrl(url: string, errorCode: number): boolean {
    const allowUnknownError = this.isOfflineUrl(url) || this.isPollingUrl(url) ? false : !errorCode;
    return this.allowedErrorUrls.some(
      (allowedErrorUrl: AllowedErrorUrl) =>
        url.includes(allowedErrorUrl.url) && (allowUnknownError || allowedErrorUrl.errorCodes.includes(errorCode)),
    );
  }

  private isOfflineUrl(url: string): boolean {
    return this.offlineUrls.some((offlineUrl: string) => url.includes(offlineUrl));
  }

  private isPollingUrl(url: string): boolean {
    return this.pollingApiUrls.some((pollingUrl: string) => url.includes(pollingUrl));
  }

  private isExpectedOfflineUrl(url: string): boolean {
    return this.isOfflineUrl(url) || this.isPollingUrl(url);
  }

  public isThumbnailUrl(url: string): boolean {
    const regex = /[?&]thumbnail=/;
    return regex.test(url);
  }
}
